import type { TFunction } from "i18next";
import type { ErrorDetails } from "../api";

type StreamErrorCode = ErrorDetails["code"];

/**
 * Structured error thrown when the chat stream reports an error event.
 *
 * `code` mirrors the backend `errorDetails.code` (e.g. "rate_limit", "content_filter",
 * "content_length", "generic") so the UI can pick the right icon/kind, while `message`
 * carries the backend-provided, user-safe text to render.
 */
export class ChatStreamError extends Error {
    code?: StreamErrorCode;

    constructor(message: string, code?: StreamErrorCode) {
        super(message);
        this.name = "ChatStreamError";
        this.code = code;
    }
}

/**
 * Presentation kind for a chat error. Drives both the localized message and the
 * icon rendered in the error alert. Mirrors the backend `errorDetails.code`.
 */
export type ChatErrorKind = "generic" | "rate_limit" | "content_filter";

/**
 * User-facing representation of a chat error: the kind (for icon selection) plus
 * the message to render.
 */
export interface ChatErrorPresentation {
    kind: ChatErrorKind;
    message: string;
}

/**
 * Maps a caught chat error to a user-facing presentation: the kind (for icon
 * selection) plus the message to render.
 *
 * The message shown is the one the backend sent (`ChatStreamError.message`,
 * populated from the stream's `error` field) so the backend stays the single
 * source of truth for wording - this lets it vary the text per situation (e.g. a
 * distinct off-topic message vs a generic content-filter block) without a frontend
 * change. `code` is used only to choose the kind (icon/color). When no
 * backend-supplied message is available (non-stream errors), a localized fallback
 * per kind is used instead.
 */
export function getChatError(error: unknown, t: TFunction): ChatErrorPresentation {
    const kind = getChatErrorKind(error);
    const backendMessage = error instanceof ChatStreamError ? error.message.trim() : "";
    if (backendMessage) {
        return { kind, message: backendMessage };
    }
    return { kind, message: fallbackMessage(kind, t) };
}

/**
 * The presentation kind for an error, derived from the backend `errorDetails.code`.
 */
function getChatErrorKind(error: unknown): ChatErrorKind {
    if (error instanceof ChatStreamError && error.code === "rate_limit") {
        return "rate_limit";
    }
    if (error instanceof ChatStreamError && error.code === "content_filter") {
        return "content_filter";
    }
    return "generic";
}

/**
 * Localized fallback message per kind, used only when the backend supplied no message.
 */
function fallbackMessage(kind: ChatErrorKind, t: TFunction): string {
    if (kind === "rate_limit") {
        return t("errorRateLimit");
    }
    if (kind === "content_filter") {
        return t("errorContentFilter");
    }
    return t("error");
}
